import { useState } from 'react';
import { estimateAVMPro } from '@/api/geo';

const TYPES_COMMERCIAUX = new Set([
  'local_commercial',
  'immeuble',
]);

const GRADE_STYLES = {
  A: 'border-green-200 bg-green-50 text-green-900',
  B: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  C: 'border-amber-200 bg-amber-50 text-amber-900',
  D: 'border-orange-200 bg-orange-50 text-orange-900',
  E: 'border-red-200 bg-red-50 text-red-900',
};

function formatEuros(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatEurosM2(value) {
  if (value == null) return '—';
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value)} €/m²`;
}

export default function AVMProEstimate({ expertiseId, typeBien, onPrefillComparaison }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!typeBien || !TYPES_COMMERCIAUX.has(typeBien)) {
    return null;
  }

  const handleEstimate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await estimateAVMPro(expertiseId);
      setResult(data);
    } catch (err) {
      const detail = err?.response?.detail || err?.message || 'Erreur lors de l\'estimation AVM Pro';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handlePrefill = () => {
    if (!result || result.expert_required || !onPrefillComparaison) return;
    if (result.valeur_estimee == null) return;
    onPrefillComparaison({
      valeur_unitaire_m2: result.prix_m2_estime != null ? Math.round(result.prix_m2_estime) : null,
      valeur_totale: Math.round(result.valeur_estimee),
    });
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Estimation AVM Pro (commercial)</h3>
        <button
          type="button"
          onClick={handleEstimate}
          disabled={loading || !expertiseId}
          className="inline-flex items-center gap-2 rounded-lg bg-carex-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-carex-700 focus:outline-none focus:ring-2 focus:ring-carex-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Estimation en cours…
            </>
          ) : (
            result ? 'Relancer l\'estimation' : 'Lancer l\'estimation AVM Pro'
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {result?.expert_required && (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2"
        >
          <p className="font-semibold">Expertise manuelle nécessaire</p>
          <p>
            {result.expert_message
              || 'Données insuffisantes pour une estimation automatique fiable. Contactez un expert pour une valorisation manuelle.'}
          </p>
          {result.nombre_comparables_trouves != null && (
            <p className="text-xs text-amber-700">
              {result.nombre_comparables_trouves} ventes trouvées dans le périmètre
              {result.perimetre_recherche ? ` (${result.perimetre_recherche})` : ''}.
            </p>
          )}
        </div>
      )}

      {result && !result.expert_required && result.valeur_estimee != null && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-carex-200 bg-carex-50 p-4">
              <p className="text-xs font-medium text-carex-600 uppercase tracking-wide">Valeur estimée</p>
              <p className="mt-1 text-xl font-bold text-carex-900">
                {formatEuros(result.valeur_estimee)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Prix / m²</p>
              <p className="mt-1 text-xl font-bold text-slate-900">
                {formatEurosM2(result.prix_m2_estime)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Fourchette</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {result.valeur_basse != null && result.valeur_haute != null
                  ? `${formatEuros(result.valeur_basse)} — ${formatEuros(result.valeur_haute)}`
                  : '—'}
              </p>
            </div>
            <div
              className={`rounded-lg border p-4 ${
                GRADE_STYLES[result.grade_confiance] || 'border-slate-200 bg-white text-slate-900'
              }`}
            >
              <p className="text-xs font-medium uppercase tracking-wide opacity-80">Confiance</p>
              <p className="mt-1 text-sm font-semibold">
                {result.grade_confiance
                  ? `${result.grade_confiance} — ${result.label_confiance || ''}`.trim()
                  : '—'}
                {result.score_confiance != null && (
                  <span className="ml-2 text-xs opacity-70">({result.score_confiance}/100)</span>
                )}
              </p>
            </div>
          </div>

          {result.nombre_comparables_trouves != null && (
            <p className="text-xs text-slate-500">
              Basée sur {result.nombre_comparables_trouves} ventes comparables
              {result.nombre_comparables_nettoyes != null
                ? `, ${result.nombre_comparables_nettoyes} retenues`
                : ''}
              {result.perimetre_recherche ? ` (périmètre : ${result.perimetre_recherche})` : ''}.
            </p>
          )}

          <div>
            <button
              type="button"
              onClick={handlePrefill}
              className="inline-flex items-center gap-2 rounded-lg border border-carex-300 bg-white px-4 py-2 text-sm font-medium text-carex-700 transition-colors duration-200 hover:bg-carex-50 focus:outline-none focus:ring-2 focus:ring-carex-500 focus:ring-offset-2"
            >
              Utiliser pour la méthode par comparaison
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
